/* eslint-env browser */
/* eslint-disable no-param-reassign */
/* eslint-disable no-unused-expressions */
import { BREAKPOINTS } from 'js/constants.js';
import { fillTemplate, handlePagination, updatePagination } from '../../../../../partner-locator/locator-shared.js';

const componentName = 'session-list';

class SessionList extends HTMLElement {

    // ==========
    // COMPONENT SETUP
    // ==========
    selectors = {
        filterDetailsContent: '.filter__details-content',
        filterSearchField: '.filter__search-field',
        resultMetadata: `.${componentName}__result-metadata`,
        resultDescription: `.${componentName}__result-description`,
    };

    elements = {
        filterDialog: this.querySelector(`.${componentName}__filter-dialog`),
        filterMobileShow: this.querySelector(`.${componentName}__filter-mobile-show`),
        filterMobileClose: this.querySelector(`.${componentName}__filter-mobile-close`),
        filterMobileSubmit: this.querySelector(`.${componentName}__filter-mobile-submit`),
        filterForm: this.querySelector(`.${componentName}__filter-form`),
        filterResets: this.querySelectorAll(`.${componentName}__filter-reset`),

        filterDetails: this.querySelectorAll('.filter__details'),
        checkboxes: this.querySelectorAll('input[type="checkbox"]'),
        facetSearches: this.querySelectorAll(`.${componentName}__facet-search`),

        filterMobileHeaderBottom: this.querySelector(`.${componentName}__filter-mobile-header-bottom`),
        filterMobileChipsContainer: this.querySelector(`.${componentName}__filter-mobile-chips-container`),
        filterDesktopChipsContainer: this.querySelector(`.${componentName}__filter-desktop-chips-container`),
        filterChipTemplate: this.querySelector(`.${componentName}__filter-chip-template`),

        desktopSortSelect: this.querySelector(`.${componentName}__desktop-sort-select`),

        resultsContentContainer: this.querySelector(`.${componentName}__results-content-container`),
        noResults: this.querySelector(`.${componentName}__no-results`),
        results: this.querySelectorAll(`.${componentName}__result`),

        paginationForm: this.querySelector(`.${componentName}__pagination-form`),
    };

    constants = Object.freeze({
        searchFilterAttribute: 'data-filtered',
        sessionSearchParam: 'sessionid',
    });

    customFormProps = Object.freeze({
        search: 'search',
        numPerPage: 'numPerPage',
        sort: 'sort',
    });

    state = {
        matchMobile: window.matchMedia(`(max-width: ${BREAKPOINTS.TABLET}px)`),
        currentPage: 1,
        numPerPage: () => Number(new FormData(this.elements.filterForm).get(this.customFormProps.numPerPage)),
        sort: () => new FormData(this.elements.filterForm).get(this.customFormProps.sort),
        sessionSet: new Set(),
    };

    // ==========
    // LOAD CONTENT
    // ==========
    runQuery = (initialLoad = false) => {
        const formData = new FormData(this.elements.filterForm);
        this.updateFilterUi(formData);

        // filter results
        const matchingResults = this.filterResults(formData);

        // sort results
        switch (this.state.sort()) {
        case 'alphaAsc':
            matchingResults.sort((a, b) => new Intl.Collator().compare(a.dataset.title, b.dataset.title));
            break;
        case 'alphaDesc':
            matchingResults.sort((a, b) => new Intl.Collator().compare(b.dataset.title, a.dataset.title));
            break;
        case 'dateAsc':
            matchingResults.sort((a, b) => new Intl.Collator().compare(a.dataset.title, b.dataset.title));
            matchingResults.sort((a, b) => this.compareDates(a.dataset.firstStartTime, b.dataset.firstStartTime));
            break;
        case 'dateDesc':
            matchingResults.sort((a, b) => new Intl.Collator().compare(a.dataset.title, b.dataset.title));
            matchingResults.sort((a, b) => this.compareDates(b.dataset.firstStartTime, a.dataset.firstStartTime));
            break;
        default:
            // do nothing
        }

        // compute pagination
        const totalResults = matchingResults.length;
        const firstResultNum = Math.min((this.state.currentPage - 1) * this.state.numPerPage() + 1, totalResults);
        const lastResultNum = Math.min(firstResultNum + this.state.numPerPage() - 1, totalResults);

        // display results page
        const matchingResultsPage = matchingResults.slice(firstResultNum - 1, lastResultNum);
        if (matchingResultsPage.length > 0) {
            this.elements.resultsContentContainer.replaceChildren(...matchingResultsPage);
        } else {
            this.elements.noResults.hidden = false;
            this.elements.resultsContentContainer.replaceChildren(this.elements.noResults);
        }

        // update pagination
        updatePagination(this.elements.paginationForm, {
            currentPageNum: this.state.currentPage,
            pageCount: Math.floor(totalResults / this.state.numPerPage()) + 1,
        });

        // update UI result counts
        fillTemplate(this, 'data-x', { firstResultNum, lastResultNum, totalResults, }, { keepIfEmpty: true });

        // update facet counts
        [...this.elements.checkboxes].forEach(this.updateCheckboxCount);

        // hide facets with zero results
        [...this.elements.filterDetails].forEach((filterFacet) => {
            filterFacet.setAttribute(
                'aria-hidden',
                filterFacet.querySelectorAll('li:not([aria-hidden="true"])').length < 1
            );
        });

        if (initialLoad) {
            // determine which filter facets should display search fields
            this.showFilterSearchFields();
        }
    };

    /**
     * @param {string} dateStringA
     * @param {string} dateStringB
     * @returns {number}
     */
    compareDates = (dateStringA, dateStringB) => {
        let dateA;
        let dateB;
        try {
            dateA = new Date(dateStringA);
        } catch {
            dateA = null;
        }
        try {
            dateB = new Date(dateStringB);
        } catch {
            dateB = null;
        }
        return dateA - dateB;
    };

    /**
     * @param {FormData | URLSearchParams} filterParams
     * @returns {HTMLElement[]}
     */
    filterResults = (filterParams) => {
        let matchingResults = [...this.elements.results];

        // Get selected tags
        const customFormPropNames = Object.values(this.customFormProps);
        const filterParamArray = [...new Set(filterParams.keys())]
            .map((key) => [key, filterParams.getAll(key).filter((value) => value !== '')]) // Remove empty filter values
            .filter(([key, values]) => !customFormPropNames.includes(key) && values.length > 0); // Remove custom form props (ones that are not metadata)

        // Get search value
        const searchQuery = filterParams.get(this.customFormProps.search)?.toLowerCase();

        matchingResults = matchingResults.filter((result) => {
            // Cache filterable data if it hasn't been cached already
            if (!result.tagCache || !result.searchCache) {
                const metadataEls = [...result.querySelectorAll(this.selectors.resultMetadata)];

                // Cache the [name, value] pairs of each tag
                result.tagCache = metadataEls.map((metadataEl) => ([metadataEl.dataset.name, metadataEl.dataset.value]));

                // Cache the searchable text: title, description, and tag titles
                const { title } = result.dataset;
                const description = result.querySelector(this.selectors.resultDescription)?.textContent;
                const metadataTitles = metadataEls.map((metadataEl) => metadataEl.dataset.title);

                result.searchCache = [title, description, ...metadataTitles]
                    .filter((searchableText) => searchableText != null)
                    .map((searchableText) => searchableText.toLowerCase());
            }

            // For each filter key, some metadata item must match one of the selected values
            const isTagMatch = filterParamArray.every(([selectedKey, selectedValues]) => result.tagCache
                .some(([metadataKey, metadataValue]) => selectedKey === metadataKey && selectedValues.includes(metadataValue)));

            // If a searchQuery is present, then it must be present within the searchable text
            const isSearchMatch = searchQuery == null || searchQuery === ''
                || result.searchCache.some((searchableText) => searchableText.includes(searchQuery));

            // Filter by session IDs
            const isSessionIdMatch = this.state.sessionSet.size === 0 || this.state.sessionSet.has(result.dataset.sessionid);

            return isTagMatch && isSearchMatch && isSessionIdMatch;
        });

        return matchingResults;
    };

    /** @param {HTMLInputElement} checkbox */
    updateCheckboxCount = (checkbox) => {
        const { name, value } = checkbox;
        const listItem = checkbox.closest('li');
        const span = listItem.querySelector('span');

        // compute result count as if this was the only option selected in its category
        const formData = new FormData(this.elements.filterForm);
        formData.set(name, value);
        const resultCount = this.filterResults(formData).length;

        span.dataset.count = resultCount;

        // check if this item was filtered by the facet search
        const isFiltered = listItem.getAttribute(this.constants.searchFilterAttribute) === 'true';
        listItem.setAttribute('aria-hidden', (resultCount < 1 && !checkbox.checked) || isFiltered);
    };

    showFilterSearchFields = () => {
        this.elements.filterDetails.forEach((facet) => {
            const numOptions = [...facet.querySelectorAll('li:not([aria-hidden="true"])')].length;
            if (numOptions > 12) {
                const filterSearchField = facet.querySelector(this.selectors.filterSearchField);
                if (filterSearchField) {
                    filterSearchField.hidden = false;
                }
            }
        });
    };

    /**
     * Get pre-selected tags from query parameters and apply the filters on the page.
     * This function also supports comma separated multiple selections.
     * @example
     * `?event=automation-fair`
     */
    readUrlParams = () => {
        // Get search params without decoding
        const urlSearchParamsString = window.location.search;
        const urlSearchParams = urlSearchParamsString.slice(1).split('&');
        urlSearchParams.forEach((searchParam) => {
            if (!searchParam.includes('=')) {
                return;
            }
            const [key, value] = searchParam.split('=');
            value.split(',').forEach((splitValue) => {
                const tagCheckbox = this.querySelector(`input[type="checkbox"][name=${decodeURIComponent(key)}][value="${decodeURIComponent(splitValue)}"]`);
                tagCheckbox?.click();
            });
        });

        const sessionListStr = urlSearchParams.find((searchParam) => searchParam.startsWith(this.constants.sessionSearchParam))?.split('=')[1];
        if (sessionListStr) {
            const sessionArr = sessionListStr.split(','); // Split the session list string into an array
            sessionArr.forEach((sessionId) => {
                this.state.sessionSet.add(decodeURIComponent(sessionId)); // Add each session ID to sessionSet
            });
        }
    };

    /** @param {URLSearchParams | FormData} searchParams */
    updateFilterUi = (searchParams) => {
        // Make chips for selected checkboxes
        const chipData = [...this.elements.checkboxes]
            .filter((checkbox) => checkbox.checked)
            .map((checkbox) => ({
                label: checkbox.closest('li').dataset.label,
                name: checkbox.name,
                value: checkbox.value,
            }));

        // Make chip for search value
        const searchValue = searchParams.get(this.customFormProps.search);
        if (searchValue != null && searchValue !== '') {
            chipData.push({
                label: searchValue,
                name: this.customFormProps.search,
                value: searchValue,
            });
        }

        // Make chip for session id
        this.state.sessionSet.forEach((sessionId) => {
            chipData.push({
                label: sessionId,
                name: this.constants.sessionSearchParam,
                value: sessionId,
            });
        });

        // Update chips
        const desktopChips = chipData.map(({ label, name, value }) => this.createFilterChip({ label, name, value }));
        const mobileChips = chipData.map(({ label, name, value }) => this.createFilterChip({ label, name, value }));
        this.elements.filterDesktopChipsContainer.replaceChildren(...desktopChips);
        this.elements.filterMobileChipsContainer.replaceChildren(...mobileChips);

        // Hide/unhide chip containers and reset buttons
        const hasChips = chipData.length > 0;
        this.elements.filterDesktopChipsContainer.hidden = !hasChips;
        this.elements.filterMobileHeaderBottom.hidden = !hasChips;
        this.elements.filterResets.forEach((el) => {
            el.disabled = !hasChips;
        });

        // Sync desktop sort select (the mobile sort radio buttons are used as source of truth)
        this.elements.desktopSortSelect.value = searchParams.get(this.customFormProps.sort);
        this.elements.desktopSortSelect.sync?.();
    };

    /**
     * @param {Record<string, string?>} params
     * @returns {HTMLElement}
     */
    createFilterChip = ({ label, name, value }) => {
        const template = this.elements.filterChipTemplate.content.cloneNode(true);
        fillTemplate(template, 'data-xt', { label, name, value });
        template.querySelector('button').addEventListener('click', this.handleChipClick);
        return template;
    };

    /** @param {MouseEvent} ev */
    handleChipClick = (ev) => {
        ev.preventDefault();
        const { name, value } = ev.currentTarget.dataset;

        if (name === this.customFormProps.search) {
            const searchInput = this.querySelector(`input[name="${this.customFormProps.search}"]`);
            searchInput.value = '';
        } else if (name === this.constants.sessionSearchParam) {
            this.state.sessionSet.delete(value);
        } else {
            const checkbox = this.querySelector(`input[type="checkbox"][name="${name}"][value="${value}"]`);
            checkbox.checked = false;
        }
        this.handleFilterForm();
    };

    // ==========
    // UI LOGIC
    // ==========
    /** @param {SubmitEvent?} ev */
    handleFilterForm = (ev = null) => {
        ev?.preventDefault();
        this.state.currentPage = 1;
        this.runQuery();
    };

    handleMatchMobileChange = () => {
        if (this.state.matchMobile.matches) {
            this.elements.filterDetails.forEach((detailsEl) => {
                detailsEl.open = false;
            });
        } else {
            this.elements.filterDialog.close();
        }
    };

    /**
     * Native implementation of jQuery slideUp/slideDown using CSS transitions
     * @param {MouseEvent} ev
     */
    handleSummaryClick = (ev) => {
        ev.preventDefault();
        const detailsEl = ev.currentTarget.closest('details');
        const contentEl = detailsEl.querySelector('div');

        if (!contentEl) return;

        if (!detailsEl.open) {
            // Open (equivalent to slideDown)
            contentEl.style.display = 'none';
            detailsEl.open = true;
            contentEl.style.display = '';

            // Force reflow then show
            contentEl.offsetHeight;
            contentEl.style.transition = 'all 0.3s ease';
            contentEl.style.maxHeight = contentEl.scrollHeight + 'px';
            contentEl.style.opacity = '1';
        } else {
            // Close (equivalent to slideUp)
            contentEl.style.transition = 'all 0.3s ease';
            contentEl.style.maxHeight = '0';
            contentEl.style.opacity = '0';

            setTimeout(() => {
                detailsEl.open = false;
                contentEl.style.transition = '';
                contentEl.style.maxHeight = '';
                contentEl.style.opacity = '';
            }, 300);
        }
    };

    showMobileFilter = () => {
        document.body.classList.add('no-scroll');
        this.elements.filterDialog.style.left = '100%';
        this.elements.filterDialog.showModal();
        this.elements.filterDialog.style.left = null;
    };

    closeMobileFilter = () => {
        document.body.classList.remove('no-scroll');
        this.elements.filterDialog.addEventListener('transitionend', () => {
            this.elements.filterDialog.close();
            this.elements.filterDialog.style.left = null;
        }, { once: true });
        this.elements.filterDialog.style.left = '100%';
    };

    /** @param {Event} ev */
    handleDesktopSortSelect = (ev) => {
        const matchingMobileSortOption = this.elements.filterForm.querySelector(
            `[name="${this.customFormProps.sort}"][value="${ev.currentTarget.value}"]`
        );
        if (matchingMobileSortOption) {
            matchingMobileSortOption.click();
        }
    };

    /** @param {Event} ev */
    handleFacetSearch = (ev) => {
        const input = ev.currentTarget;
        const inputValue = input.value?.toLowerCase();

        const listItems = input.closest(this.selectors.filterDetailsContent).querySelectorAll('li');

        listItems.forEach((listItem) => {
            const isMatch = listItem.dataset.label.toLowerCase().includes(inputValue);
            const noResults = listItem.querySelector('span').dataset.count < 1;
            listItem.setAttribute(this.constants.searchFilterAttribute, !isMatch);
            listItem.setAttribute('aria-hidden', !isMatch || noResults);
        });
    };

    handlePagination = handlePagination({
        handlePrevButton: () => this.state.currentPage -= 1,
        handleNextButton: () => this.state.currentPage += 1,
        handleInput: (input) => this.state.currentPage = input,
        then: this.runQuery,
    });

    // ==========
    // ADD EVENT LISTENERS
    // ==========
    addEventListeners = () => {
        // Filter details summary click handlers
        this.querySelectorAll('.filter__details summary')
            .forEach((el) => el.addEventListener('click', this.handleSummaryClick));

        this.elements.filterMobileShow.addEventListener('click', this.showMobileFilter);
        this.elements.filterMobileClose.addEventListener('click', this.closeMobileFilter);
        this.elements.filterMobileSubmit.addEventListener('click', this.closeMobileFilter);

        this.elements.filterForm.addEventListener('submit', this.handleFilterForm);
        this.elements.filterForm.addEventListener('reset', () => {
            this.state.sessionSet.clear();
            window.setTimeout(this.handleFilterForm);
        });
        [...this.elements.filterForm.elements].forEach((formControl) => {
            formControl.addEventListener('change', this.handleFilterForm);
        });

        this.elements.facetSearches.forEach((el) => el.addEventListener('input', this.handleFacetSearch));
        this.elements.desktopSortSelect?.addEventListener('change', this.handleDesktopSortSelect);

        // Pagination
        this.elements.paginationForm.addEventListener('submit', this.handlePagination);

        this.state.matchMobile.addEventListener('change', this.handleMatchMobileChange);
    };

    // ==========
    // INITIALIZE COMPONENT
    // ==========
    connectedCallback() {
        this.readUrlParams();
        this.addEventListeners();
        this.handleMatchMobileChange();
        this.runQuery(true);
        window.sessionListInstance = this; // for debug purposes
    }
}

window.customElements.define(componentName, SessionList);
